import React from 'react';
import useGameStore from '@store/useGameStore';
import useMetadataStore from '@store/useMetadataStore';
import UnitIcon from '@gameobjects/UnitIcon';
import ActionIcon from '@components/ui/game/action-icon/ActionIcon';
import XhelosTooltip from '@components/ui/general/xhelos-tooltip/XhelosTooltip';
import { getEffectIcon } from '@utils/statIcons';
import { EffectPhaseDisplay } from '@utils/enums';
import './EventCard.css';

/**
 * Reusable play button overlay component
 * Shows play icon on hover when event is selected
 */
const PlayButtonOverlay = ({ show, isArtificial }) => {
  if (!show || isArtificial) return null;
  
  return (
    <div className="play-button-overlay">
      <svg className="play-icon" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z"/>
      </svg>
    </div>
  );
};

/**
 * Event Card Component for Cinematic Timeline
 * Handles different event types: actions, effects, etc.
 * Displays unit icon on top and event-specific icon below
 */
const EventCard = ({ event, index, isSelected, isExecuted, isAnimating, showPlayButton, onClick }) => {
  
  // Determine event type
  const eventType = event.type; // Default to action for backward compatibility
  const isArtificial = event.isArtificial;
  
  // Common card states
  const isCurrentlyAnimating = isAnimating;
  const canShowPlayButton = showPlayButton; // CSS will handle hover visibility
  
  // Base classes for all event types
  const getCardState = () => {
    if (isSelected) return 'active';
    if (isExecuted) return 'completed';
    return 'pending';
  };
  
  const cardState = getCardState();
  const baseCardClasses = [
    'timeline-event-card',
    cardState,
    isCurrentlyAnimating ? 'animating' : '',
    isArtificial ? 'artificial' : '',
    event.teamClass ? `team-${event.teamClass}` : '',
    eventType === 'effect_processing' || eventType === 'effect_application' ? 'effect-event' : ''
  ].filter(Boolean).join(' ');
  
  // Get unit information (common for all event types)
  const unit = event.unit_id ? useGameStore.getState().getUnitById(event.unit_id) : null;
  //console.log('[EventCard] Rendering event:', event);
 /* if(!event.isArtificial){
    console.log('[EventCard] Rendering unit:', unit);
    console.log('[EventCard] Rendering unit id:', unit.id);
  }*/
  // Handle click - parent component manages the logic
  const handleClick = () => {
    if (!isAnimating) {
      onClick();
    }
  };
  
  // Switch based on event type
  switch (eventType) {
    case 'action':
      return renderActionEvent();
    case 'effect_processing':
    case 'effect_application':
    case 'effect_removal':
      return renderEffectEvent();
    case 'current_state':
      return renderCurrentStateEvent();
    default:
      return renderUnknownEvent();
  }
  
  // ================ RENDER FUNCTIONS ================
  
  // Render action event (move, attack, etc.)
  function renderActionEvent() {
    // Guard clause - Check if we have required data
    if (!isArtificial && !event.action_id) {
      console.warn('[EventCard] Missing action_id for action event:', event);
      return renderUnknownEvent();
    }
    
    // Get action definition using action_id
    let actionDefinition = null;
    let actionName = 'Unknown Action';
    let actionSlug = 'unknown';
    
    if (!isArtificial) {
      actionDefinition = useGameStore.getState().getActionById(event.action_id);
      if (!actionDefinition) {
        console.warn('[EventCard] Action definition not found for action_id:', event.action_id, {
          event,
          availableActions: Object.keys(useGameStore.getState().game?.actions || {})
        });
      } else {
        actionName = actionDefinition.name;
        actionSlug = actionDefinition.slug;
        
        // If this is a reaction/counter, add "Counter: " prefix
        if (event.is_reaction) {
          actionName = `Counter: ${actionName}`;
        }
      }
    } else {
      // For artificial events, use the provided name
      actionName = event.action?.name || 'State';
    }
  
  // Get target information - check if we hit a unit or missed
  let targetDisplay = null;
  let wasMissed = false;
  
  
  if (event.target_data) {
    // Check resolution_details for defender information
    if (event.resolution_details?.defender_id) {
      const targetUnit = useGameStore.getState().getUnitById(event.resolution_details.defender_id);
      if (targetUnit) {
        targetDisplay = targetUnit.name;
      } else {
        targetDisplay = `Unit #${event.resolution_details.defender_id}`;
      }
    } else if (event.resolution_details?.target_unit_id) {
      // Some actions might use target_unit_id instead
      const targetUnit = useGameStore.getState().getUnitById(event.resolution_details.target_unit_id);
      if (targetUnit) {
        targetDisplay = targetUnit.name;
      } else {
        targetDisplay = `Unit #${event.resolution_details.target_unit_id}`;
      }
    } else {
      // No unit hit - show coordinates
      targetDisplay = `(${event.target_data.x}, ${event.target_data.y})`;
      
      // Check if this was a missed attack
      if (event.resolution_details?.action === 'attack_missed' || 
          event.resolution_details?.was_missed === true ||
          event.resolution_details?.missed === true) {
        wasMissed = true;
      }
    }
  }

  // Tooltip content with improved structure
  const tooltipContent = (
    <div className="card-tooltip-content card-tooltip-content-action">
      <div className="card-tooltip-content-name">{actionName}</div>
      {unit && <div className="card-tooltip-content-row">Origin: {unit.name}</div>}
      {targetDisplay && (
        <div className="card-tooltip-content-row">
          Target: {targetDisplay}
          {wasMissed && <span className="card-tooltip-content-row missed"> (Missed)</span>}
        </div>
      )}
      {event.resolution_details?.damage_dealt !== undefined && event.resolution_details?.damage_dealt > 0 && (
        <div className="card-tooltip-content-row">Damage: {event.resolution_details.damage_dealt}</div>
      )}
      {event.resolution_details?.was_killed && (
        <div className="card-tooltip-content-row killed">Target Destroyed!</div>
      )}
      {event.ap_cost > 0 && (
        <div className="card-tooltip-content-row">AP Cost: {event.ap_cost}</div>
      )}
      {event.energy_cost > 0 && (
        <div className="card-tooltip-content-row">Energy Cost: {event.energy_cost}</div>
      )}
    </div>
  );

  // Handle click - parent component manages the logic
  const handleClick = () => {
    if (!isAnimating) {
      onClick();
    }
    // If animating, ignore clicks
  };
  
  // Wrap the card with XhelosTooltip for nice popup on top
  const cardElement = (
    <div className={baseCardClasses} onClick={handleClick}>
      {/* Step indicator */}
      <div className="card-step-indicator">{index}</div>
      
      {/* Unit Icon on Top */}
      <div className="card-unit-section">
        {isArtificial ? (
          <div className="artificial-icon">
            {actionName === 'Initial State' ? '🏁' : '🎯'}
          </div>
        ) : unit ? (
          <UnitIcon unitId={unit.id} size="small" clickable={false} />
        ) : (
          <div className="unknown-unit">❓</div>
        )}
      </div>
      
      {/* Play Button Overlay - shows on hover when selected */}
      <PlayButtonOverlay show={canShowPlayButton} isArtificial={isArtificial} />
      
      {/* Removed circular progress overlay - action icon will rotate instead */}
      
      {/* Action Icon Below - Using the ActionIcon component */}
      <div className="card-action-section">
        {isArtificial ? (
          <div className="artificial-label">
            {actionName === 'Initial State' ? 'Start' : 'End'}
          </div>
        ) : (
          <>
            <ActionIcon 
              action={actionDefinition}
              actionId={actionSlug}
              size={24}
              className={`timeline-action-icon ${isCurrentlyAnimating ? 'animating' : ''}`}
              teamClass={event.teamClass}
              showFallback={true}
            />
            <div className="action-name-small">
              {actionName.length > 8 ? actionName.substring(0, 6) + '...' : actionName}
            </div>
          </>
        )}
      </div>
    </div>
  );
  
    // Show tooltip on hover for non-artificial cards
    if (!isArtificial) {
      return (
        <XhelosTooltip
          content={tooltipContent}
          placement="top"
          theme="dark"
          hasArrow={true}
          trigger="hover"
          interactive={false}
        >
          {cardElement}
        </XhelosTooltip>
      );
    }
    
    return cardElement;
  }
  
  // Render effect event (unified for processing, application, and removal)
  function renderEffectEvent() {
    // Get effect definition from metadata store
    const effectDefinition = useMetadataStore.getState().getEffectBySlug(event.effect_slug);
    const effectName = effectDefinition?.name || event.effect_slug || 'Effect';
    const effectIcon = getEffectIcon(event.effect_slug);

    // Get display name from enum mapping
    const phaseName = EffectPhaseDisplay[event.phase] || 'Unknown';

    if (!event.phase) {
      console.warn('[EventCard] Effect event missing phase:', event);
    }

    // Build tooltip details array
    const details = [];

    // Add damage/healing/stats to details
    if (event.damage_dealt) {
      details.push(`Damage: ${event.damage_dealt}`);
    }
    if (event.healing_done) {
      details.push(`Healing: ${event.healing_done}`);
    }
    if (event.stat_modifiers && Object.keys(event.stat_modifiers).length > 0) {
      const statsText = Object.entries(event.stat_modifiers)
        .map(([stat, value]) => `${stat} ${value > 0 ? '+' : ''}${value}`)
        .join(', ');
      details.push(`Stats: ${statsText}`);
    }
    if (event.turns_remaining !== undefined) {
      details.push(`Turns remaining: ${event.turns_remaining}`);
    }
    if (event.duration !== undefined && event.duration > 0) {
      details.push(`Duration: ${event.duration} turns`);
    }
    if (event.source_name) {
      details.push(`Source: ${event.source_name}`);
    }

    // Tooltip content
    const tooltipContent = (
      <div className="card-tooltip-content card-tooltip-content-effect">
        <div className="card-tooltip-content-name">{phaseName}: {effectName}</div>
        {unit && <div className="card-tooltip-content-row">Unit: {unit.name}</div>}
        {details.map((detail, i) => (
          <div key={i} className="card-tooltip-content-row">{detail}</div>
        ))}
      </div>
    );

    const cardElement = (
      <div className={baseCardClasses} onClick={handleClick}>
        {/* Step indicator */}
        <div className="card-step-indicator">{index}</div>

        {/* Unit Icon */}
        <div className="card-unit-section">
          {unit ? (
            <UnitIcon unitId={unit.id} size="small" clickable={false} />
          ) : (
            <div className="unknown-unit">❓</div>
          )}
        </div>

        {/* Play Button Overlay - shows on hover when selected */}
        <PlayButtonOverlay show={canShowPlayButton} isArtificial={isArtificial} />

        {/* Effect Icon */}
        <div className="card-action-section">
          <div className="effect-icon">{effectIcon}</div>
          <div className="action-name-small">{phaseName}</div>
        </div>
      </div>
    );

    return (
      <XhelosTooltip content={tooltipContent} placement="top">
        {cardElement}
      </XhelosTooltip>
    );
  }
  
  // Render current state event (shows current game state)
  function renderCurrentStateEvent() {
    const tooltipContent = (
      <div className="card-tooltip-content">
        <div className="card-tooltip-content-name">Current Game State</div>
        <div className="card-tooltip-content-row">This represents the current state of the game</div>
        <div className="card-tooltip-content-row">All previous events have been executed</div>
      </div>
    );

    const cardElement = (
      <div className={baseCardClasses} onClick={handleClick}>
        {/* Step indicator */}
        <div className="card-step-indicator">current</div>

        {/* Current state icon */}
        <div className="card-unit-section">
          <div className="artificial-icon">🎯</div>
        </div>

        {/* Play Button Overlay */}
        <PlayButtonOverlay show={canShowPlayButton} isArtificial={true} />

        {/* Label */}
        <div className="card-action-section">
          <div className="artificial-label">Current</div>
        </div>
      </div>
    );

    return (
      <XhelosTooltip content={tooltipContent} placement="top">
        {cardElement}
      </XhelosTooltip>
    );
  }

  // Render unknown/unsupported event type
  function renderUnknownEvent() {
    const tooltipContent = (
      <div className="card-tooltip-content">
        <div className="card-tooltip-content-name">Unknown Event</div>
        <div className="card-tooltip-content-row">Type: {eventType}</div>
        <div className="card-tooltip-content-row">⚠️ This event type is not yet implemented</div>
      </div>
    );
    
    const cardElement = (
      <div className={baseCardClasses} onClick={handleClick}>
        {/* Step indicator */}
        <div className="card-step-indicator">{index}</div>
        
        {/* Unit Icon */}
        <div className="card-unit-section">
          {unit ? (
            <UnitIcon unitId={unit.id} size="small" />
          ) : (
            <div className="unknown-unit">❓</div>
          )}
        </div>
        
        {/* Play Button Overlay - shows on hover when selected */}
        <PlayButtonOverlay show={canShowPlayButton} isArtificial={isArtificial} />
        
        {/* Unknown Icon */}
        <div className="card-action-section">
          <div className="unknown-icon">⚠️</div>
          <div className="action-name-small">{eventType}</div>
        </div>
      </div>
    );
    
    return (
      <XhelosTooltip content={tooltipContent} placement="top">
        {cardElement}
      </XhelosTooltip>
    );
  }
};

export default React.memo(EventCard);